import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Coins, Zap, RotateCcw, ArrowRight, Home as HomeIcon, PartyPopper } from "lucide-react";
import { TOPIC_MAP, LEVELS } from "@/lib/topics";
import { useClient } from "@/context/ClientContext";

export default function Results() {
  const navigate = useNavigate();
  const { lastResult, clientId } = useClient();

  useEffect(() => {
    if (!lastResult) navigate(`/clients/${clientId}`);
  }, [lastResult, navigate, clientId]);

  if (!lastResult) return null;

  const { correct, total, maxStreak, stars, passed, earnedXp, earnedCoins, topicId, difficulty, mode } = lastResult;
  const topic = TOPIC_MAP[topicId];
  const levelIds = LEVELS.map((l) => l.id);
  const nextIdx = levelIds.indexOf(difficulty) + 1;
  const hasNext = passed && nextIdx < levelIds.length;

  return (
    <div className="relative overflow-hidden min-h-[calc(100vh-4rem)]">
      <div className="pointer-events-none absolute inset-0 flex justify-center">
        <div className="w-[500px] h-[500px] rounded-full blur-3xl opacity-30" style={{ background: passed ? "#34D399" : topic.color }} />
      </div>

      <div className="relative max-w-md mx-auto px-6 pt-10 pb-24 text-center">
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 14 }}
          className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-5 border-b-8"
          style={{
            background: passed ? "#34D399" : "#FB7185",
            borderColor: passed ? "#059669" : "#E11D48",
          }}
        >
          <PartyPopper className="w-12 h-12 text-white" strokeWidth={2.5} />
        </motion.div>

        <h1 className="font-display text-4xl font-bold text-[#005580] mb-1">
          {passed ? "Level Cleared!" : "So Close!"}
        </h1>
        <p className="text-slate-500 font-semibold mb-6">
          {topic.name} · {LEVELS.find((l) => l.id === difficulty)?.name} · {mode === "scenario" ? "Scenario" : "Quiz"}
        </p>

        {/* stars */}
        <div className="flex justify-center gap-2 mb-8" data-testid="result-stars">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              initial={{ scale: 0, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.15, type: "spring", stiffness: 300, damping: 12 }}
            >
              <Star className={`w-12 h-12 ${i < stars ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-200"}`} />
            </motion.div>
          ))}
        </div>

        {/* stats grid */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <ResultStat testid="result-score" label="Score" value={`${correct}/${total}`} icon={<Zap className="w-4 h-4 text-sky-500" />} />
          <ResultStat testid="result-xp" label="XP" value={`+${earnedXp}`} icon={<Star className="w-4 h-4 fill-sky-400 text-sky-500" />} />
          <ResultStat testid="result-coins" label="Coins" value={`+${earnedCoins}`} icon={<Coins className="w-4 h-4 fill-amber-400 text-amber-500" />} />
        </div>

        <div className="space-y-3">
          {hasNext && (
            <button
              data-testid="next-level-btn"
              onClick={() => navigate(`/clients/${clientId}/play/${topicId}/${levelIds[nextIdx]}/${mode}`)}
              className="w-full text-white font-bold rounded-2xl py-4 border-b-4 active:border-b-0 active:translate-y-[3px] transition-all flex items-center justify-center gap-2"
              style={{ background: topic.color, borderColor: topic.deep }}
            >
              Next level <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </button>
          )}
          <button
            data-testid="play-again-btn"
            onClick={() => navigate(`/clients/${clientId}/play/${topicId}/${difficulty}/${mode}`)}
            className="w-full font-bold rounded-2xl py-4 border-2 border-b-4 border-slate-200 bg-white text-slate-700 active:border-b-2 active:translate-y-[2px] transition-all flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" strokeWidth={2.5} /> Play again
          </button>
          <button
            data-testid="back-to-levels-btn"
            onClick={() => navigate(`/clients/${clientId}/topic/${topicId}`)}
            className="w-full font-bold rounded-2xl py-3 text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center gap-2"
          >
            <HomeIcon className="w-4 h-4" /> Back to levels
          </button>
        </div>
      </div>
    </div>
  );
}

const ResultStat = ({ label, value, icon, testid }) => (
  <div className="bg-white border-2 border-slate-100 rounded-2xl p-3 shadow-sm" data-testid={testid}>
    <div className="flex justify-center mb-1">{icon}</div>
    <div className="font-display font-bold text-lg text-slate-800 tabular-nums leading-none">{value}</div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mt-1">{label}</div>
  </div>
);
